import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import {
  BaseDetailBulkItemDataFragment,
  DeleteBulkMessageItemDocument,
  GetBulkItemsDocument,
} from '../../../../data/types/api/graphql';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';

type BulkMessagesDeletePromptProps = {
  item: BaseDetailBulkItemDataFragment;
  onDeleted?: (value: BaseDetailBulkItemDataFragment) => void;
  onClose?: () => void;
};

export function BulkMessageDeletePrompt({
  item,
  onDeleted,
  onClose,
}: BulkMessagesDeletePromptProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const [deleteBulkItem] = useMutation(DeleteBulkMessageItemDocument, {
    refetchQueries: [GetBulkItemsDocument],
  });

  const onHandleDelete = useCallback(async () => {
    try {
      const result = await deleteBulkItem({
        variables: { id: item.id },
      });

      if (result.data?.bulkCampaignDelete.boolean) {
        showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: intl.formatMessage({
            id: 'bulk-messages.bulk-message-delete.success',
          }),
        });

        onDeleted?.(item);

        onClose?.();
      } else if (result.errors) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: result.errors.map(option => option.message).join(','),
        });
      }
    } catch (error) {
      showAlertMessage({
        type: AlertMessageType.ERROR,
        message: (error as Error).message,
      });
    }
  }, [deleteBulkItem, item, showAlertMessage, intl, onDeleted, onClose]);

  if (!item) return null;

  return (
    <ModalPrompt
      testId="bulk-message-delete"
      onConfirm={onHandleDelete}
      onClose={onClose}
    >
      <p>
        <CustomFormattedMessage id="bulk-messages.bulk-message-delete.content" />
      </p>
    </ModalPrompt>
  );
}
